import { useState, useEffect } from "react";
import { ShieldCheck, Upload, CheckCircle2, Clock, XCircle, Loader2, FileText } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { clsx } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { COUNTRIES } from "../lib/countries";

const DOCS = [
  { key: "national_id", label: "National ID" },
  { key: "passport", label: "Passport" },
  { key: "drivers_license", label: "Driver's License" },
  { key: "selfie", label: "Selfie with ID" },
  { key: "supporting", label: "Supporting Document" },
];

const STATUS = {
  not_started: ["Not started", "text-fx-s3 bg-white/5", Clock],
  under_review: ["Under review", "text-gold bg-gold/15", Clock],
  approved: ["Approved", "text-pos bg-pos/15", CheckCircle2],
  rejected: ["Rejected", "text-neg bg-neg/15", XCircle],
  resubmit_required: ["Resubmit required", "text-gold bg-gold/15", XCircle],
};

export default function Kyc() {
  const [status, setStatus] = useState("not_started");
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState({ full_name: "", dob: "", country: "", address: "", national_id: "" });
  const [docs, setDocs] = useState({});
  const [uploading, setUploading] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/kyc/me").then((r) => { setStatus(r.data.status); setRecord(r.data.record); });
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadDoc = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const fd = new FormData();
      fd.append("doc_type", key);
      fd.append("file", file);
      const { data } = await api.post("/kyc/upload", fd);
      setDocs((d) => ({ ...d, [key]: { file_id: data.file_id, filename: data.filename } }));
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(null); }
  };

  const submit = async () => {
    if (Object.keys(docs).length === 0) { toast.error("Upload at least one document"); return; }
    if (!form.full_name || !form.dob || !form.country) { toast.error("Fill in your details"); return; }
    setSubmitting(true);
    try {
      const documents = Object.fromEntries(Object.entries(docs).map(([k, v]) => [k, v.file_id]));
      await api.post("/kyc/submit", { ...form, documents });
      toast.success("KYC submitted for review");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submit failed");
    } finally { setSubmitting(false); }
  };

  const [sLabel, sCls, SIcon] = STATUS[status] || STATUS.not_started;
  const locked = status === "under_review" || status === "approved";

  return (
    <div className="p-4 lg:p-8 space-y-6 max-w-3xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-brandblue" /><h1 className="font-display text-3xl font-extrabold tracking-tight">Identity Verification</h1></div>
        <span className={clsx("px-3 py-1 rounded-full text-xs font-mono inline-flex items-center gap-1.5", sCls)} data-testid="kyc-status"><SIcon className="h-3.5 w-3.5" /> {sLabel}</span>
      </div>

      {record?.rejection_reason && (status === "rejected" || status === "resubmit_required") && (
        <div className="rounded-lg bg-neg/10 border border-neg/30 p-4 text-sm text-neg" data-testid="kyc-reason">
          Reviewer note: {record.rejection_reason}
        </div>
      )}

      {status === "approved" ? (
        <div className="rounded-xl bg-pos/10 border border-pos/30 p-8 text-center">
          <CheckCircle2 className="h-12 w-12 text-pos mx-auto" />
          <h2 className="font-display font-bold text-xl mt-3">You're verified</h2>
          <p className="text-fx-s3 mt-1">Your identity has been approved. All verification-gated features are unlocked.</p>
        </div>
      ) : (
        <>
          <div className="rounded-xl bg-fx-s1 border border-white/5 p-6 grid md:grid-cols-2 gap-4">
            <div className="space-y-1"><Label>Full legal name</Label><Input data-testid="kyc-fullname" disabled={locked} value={form.full_name} onChange={upd("full_name")} className="bg-fx-base border-white/10" /></div>
            <div className="space-y-1"><Label>Date of birth</Label><Input data-testid="kyc-dob" type="date" disabled={locked} value={form.dob} onChange={upd("dob")} className="bg-fx-base border-white/10" /></div>
            <div className="space-y-1"><Label>Country</Label>
              <select data-testid="kyc-country" disabled={locked} value={form.country} onChange={upd("country")} className="w-full h-10 rounded-md bg-fx-base border border-white/10 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brandblue/50 disabled:opacity-60">
                <option value="">Select country</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-1"><Label>National ID number</Label><Input data-testid="kyc-idnum" disabled={locked} value={form.national_id} onChange={upd("national_id")} className="bg-fx-base border-white/10" /></div>
            <div className="space-y-1 md:col-span-2"><Label>Residential address</Label><Input data-testid="kyc-address" disabled={locked} value={form.address} onChange={upd("address")} className="bg-fx-base border-white/10" /></div>
          </div>

          <div className="rounded-xl bg-fx-s1 border border-white/5 p-6 space-y-3">
            <h2 className="font-display font-bold">Documents</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {DOCS.map((d) => (
                <label key={d.key} data-testid={`kyc-doc-${d.key}`} className={clsx("flex items-center gap-3 rounded-lg border border-dashed border-white/15 p-3 cursor-pointer hover:border-brandblue/50 transition-colors", locked && "opacity-50 pointer-events-none")}>
                  <div className="h-9 w-9 rounded bg-fx-base grid place-items-center text-brandblue">
                    {uploading === d.key ? <Loader2 className="h-4 w-4 animate-spin" /> : docs[d.key] ? <CheckCircle2 className="h-4 w-4 text-pos" /> : <Upload className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{d.label}</div>
                    <div className="text-xs text-fx-s3 truncate">{docs[d.key]?.filename || "Image or PDF, max 8MB"}</div>
                  </div>
                  <input type="file" accept="image/*,.pdf" className="hidden" disabled={locked} onChange={(e) => uploadDoc(d.key, e.target.files[0])} />
                </label>
              ))}
            </div>
          </div>

          {!locked && (
            <Button onClick={submit} disabled={submitting} data-testid="kyc-submit-btn" className="bg-brandblue hover:bg-blue-500 gap-2">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Submit for verification
            </Button>
          )}
          {status === "under_review" && <p className="text-sm text-gold">Your documents are under review. You'll be notified once a decision is made.</p>}
        </>
      )}
    </div>
  );
}
